import { forwardRef, type InputHTMLAttributes } from 'react'
import { clsx } from 'clsx'

export interface RadioOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

export interface RadioGroupProps {
  name: string
  label?: string
  options: RadioOption[]
  value?: string
  onChange?: (value: string) => void
  error?: string
  className?: string
}

export function RadioGroup({
  name,
  label,
  options,
  value,
  onChange,
  error,
  className,
}: RadioGroupProps) {
  return (
    <fieldset className={className}>
      {label && <legend className="text-sm font-medium text-neutral-700 mb-3">{label}</legend>}
      <div className="space-y-3">
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description}
            checked={value === option.value}
            onChange={() => onChange?.(option.value)}
            disabled={option.disabled}
            error={!!error}
          />
        ))}
      </div>
      {error && <p className="mt-2 text-sm text-danger-600">{error}</p>}
    </fieldset>
  )
}

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  description?: string
  error?: boolean
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ className, label, description, error, id, ...props }, ref) => {
    const radioId = id || `${props.name}-${props.value}`

    return (
      <div className="relative flex items-start">
        <div className="flex h-6 items-center">
          <input
            ref={ref}
            type="radio"
            id={radioId}
            className={clsx(
              'h-4 w-4 border bg-white cursor-pointer',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500',
              'checked:border-primary-600 checked:bg-primary-600',
              error ? 'border-danger-500' : 'border-neutral-300',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              className
            )}
            {...props}
          />
        </div>
        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={radioId}
                className={clsx(
                  'text-sm font-medium cursor-pointer',
                  error ? 'text-danger-700' : 'text-neutral-700'
                )}
              >
                {label}
              </label>
            )}
            {description && <p className="text-sm text-neutral-500">{description}</p>}
          </div>
        )}
      </div>
    )
  }
)

Radio.displayName = 'Radio'
